import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  vouchers: [],
  isLoading: false,
  error: null,
  filters: {
    search: '',
    status: '',
    associationId: '',
    ownerUserId: '',
    from: '',
    to: '',
  },
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  },
};

const voucherSlice = createSlice({
  name: 'voucher',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    setPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    setLimit: (state, action) => {
      state.pagination.limit = action.payload;
    },
    setCurrentPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    setItemsPerPage: (state, action) => {
      state.pagination.limit = action.payload;
      state.pagination.page = 1; // Reset to page 1
    },
    resetPagination: (state) => {
      state.pagination = {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Vouchers
      .addCase('voucher/getVouchers/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('voucher/getVouchers/fulfilled', (state, action) => {
        state.isLoading = false;

        if (Array.isArray(action.payload)) {
          state.vouchers = action.payload;
        } else if (action.payload?.data) {
          state.vouchers = action.payload.data;
          if (action.payload.pagination) {
            state.pagination = {
              page: action.payload.pagination.page || state.pagination.page,
              limit: action.payload.pagination.limit || state.pagination.limit,
              total: action.payload.pagination.total || 0,
              totalPages: action.payload.pagination.totalPages || 0,
              hasNext: action.payload.pagination.hasNext || false,
              hasPrev: action.payload.pagination.hasPrev || false,
            };
          }
        } else {
          state.vouchers = Array.isArray(action.payload) ? action.payload : [];
        }

        state.error = null;
      })
      .addCase('voucher/getVouchers/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch vouchers';
      })
      // Issue Manual Voucher
      .addCase('voucher/issueVoucher/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('voucher/issueVoucher/fulfilled', (state, action) => {
        state.isLoading = false;
        const newVoucher = action.payload?.data || action.payload;
        state.vouchers.unshift(newVoucher); // Add issued voucher to the beginning
        state.error = null;
      })
      .addCase('voucher/issueVoucher/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to issue manual voucher';
      })
      // Update Voucher
      .addCase('voucher/updateVoucher/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('voucher/updateVoucher/fulfilled', (state, action) => {
        state.isLoading = false;
        const updatedVoucher = action.payload?.data || action.payload;
        const index = state.vouchers.findIndex((item) => {
          const itemId = item._id || item.id;
          const updatedId = updatedVoucher._id || updatedVoucher.id;
          return itemId && itemId === updatedId;
        });
        if (index !== -1) {
          const existingVoucher = state.vouchers[index];
          state.vouchers[index] = {
            ...existingVoucher,
            ...updatedVoucher,
            ownerUserId: (typeof updatedVoucher.ownerUserId === 'object' && updatedVoucher.ownerUserId !== null)
              ? updatedVoucher.ownerUserId
              : existingVoucher.ownerUserId
          };
        }
        state.error = null;
      })
      .addCase('voucher/updateVoucher/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to update voucher';
      })
      // Share / Email Voucher
      .addCase('voucher/shareVoucher/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('voucher/shareVoucher/fulfilled', (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase('voucher/shareVoucher/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to email voucher code';
      });
  },
});

export const {
  clearError,
  setFilters,
  setPagination,
  setPage,
  setLimit,
  setCurrentPage,
  setItemsPerPage,
  resetPagination,
} = voucherSlice.actions;

export default voucherSlice.reducer;
